import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useIsAdminTier } from '@/shared/auth/SessionProvider';
import { useCanSeeMenu } from '@/shared/auth/useMenuPermissions';
import { Search } from 'lucide-react';
import { Input } from '@/shared/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/components/ui/select';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { cn } from '@/shared/lib/cn';
import { formatMoney, type CurrencyCode } from '@/shared/lib/money';
import {
  ESTADO_UNIDAD,
  etiquetaOrigen,
  etiquetaSalida,
  fechaUnidad,
  getUnidadesArticulo,
  getUnidadesCompra,
  getUnidadesImportacion,
  type SalidaUnidad,
  type UnidadStock,
} from '../stockUnidades';

/**
 * Stock por origen. Dos vistas del mismo dato (`stock_unidades`):
 *  - `StockPorOrigenTab`: de un artículo, de qué compra / importación vino cada
 *    unidad y cuánto queda (pestaña del detalle de Inventario).
 *  - `OrigenUnidadesSection`: de una compra o una importación, qué unidades
 *    entraron y a qué ventas fueron.
 */

const NO_VISIBLE = 'Todavía no hay unidades cargadas (falta aplicar la migración de stock por origen).';

const num = (n: number) => String(Math.round(n * 1000) / 1000).replace('.', ',');

function usePermisosLinks() {
  const adminTier = useIsAdminTier();
  const veCompras = useCanSeeMenu('compras');
  const veImportaciones = useCanSeeMenu('importaciones');
  return { compras: adminTier && veCompras, importaciones: adminTier && veImportaciones };
}

function EstadoPill({ estado }: { estado: UnidadStock['estado'] }) {
  return (
    <span
      className={cn(
        'inline-flex whitespace-nowrap rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
        estado === 'en_stock' && 'bg-emerald-100 text-emerald-700',
        estado === 'en_prestamo' && 'bg-amber-100 text-amber-700',
        estado === 'agotada' && 'bg-slate-200 text-slate-600',
      )}
    >
      {ESTADO_UNIDAD[estado]}
    </span>
  );
}

function OrigenCell({ u, links }: { u: UnidadStock; links: { compras: boolean; importaciones: boolean } }) {
  if (u.origen !== 'compra') return <span className="text-muted-foreground">{etiquetaOrigen(u)}</span>;
  return (
    <div className="space-y-0.5">
      {u.importacion &&
        (links.importaciones ? (
          <Link to={`/importaciones/${u.importacion.id}`} className="block font-medium text-primary hover:underline">
            Importación #{u.importacion.numero}
          </Link>
        ) : (
          <span className="block font-medium">Importación #{u.importacion.numero}</span>
        ))}
      {u.compra &&
        (links.compras ? (
          <Link to={`/compras/${u.compra.id}`} className="block text-primary hover:underline">
            Compra {u.compra.id}
            {u.compra.proveedor ? ` · ${u.compra.proveedor}` : ''}
          </Link>
        ) : (
          <span className="block">
            Compra {u.compra.id}
            {u.compra.proveedor ? ` · ${u.compra.proveedor}` : ''}
          </span>
        ))}
      {!u.importacion && <span className="block text-xs text-muted-foreground">Sin importación</span>}
      {u.nroRemito && <span className="block text-xs text-muted-foreground">Remito {u.nroRemito}</span>}
    </div>
  );
}

function Th({ children, className }: { children?: React.ReactNode; className?: string }) {
  return <th className={cn('px-2 py-2 font-medium', className)}>{children}</th>;
}

function Total({ titulo, valor }: { titulo: string; valor: string }) {
  return (
    <div className="rounded-lg border bg-muted/20 px-3 py-2">
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{titulo}</p>
      <p className="text-base font-semibold tabular-nums">{valor}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Artículo → stock por origen
// ---------------------------------------------------------------------------

type FiltroUnidades = 'stock' | 'prestadas' | 'despachadas' | 'todas';

const FILTROS: Record<FiltroUnidades, { label: string; pasa: (u: UnidadStock) => boolean }> = {
  // Lo útil para vender / despachar: lo que sigue siendo nuestro (en depósito o afuera prestado).
  stock: { label: 'En stock', pasa: (u) => u.estado !== 'agotada' },
  prestadas: { label: 'Prestadas / showroom', pasa: (u) => u.cantidadPrestada > 0 },
  despachadas: { label: 'Despachadas', pasa: (u) => u.estado === 'agotada' },
  todas: { label: 'Todas', pasa: () => true },
};

export function StockPorOrigenTab({ inventarioId }: { inventarioId: string }) {
  const links = usePermisosLinks();
  const adminTier = useIsAdminTier();
  const veVentas = useCanSeeMenu('ventas');
  const [filtro, setFiltro] = useState<FiltroUnidades>('stock');
  const [buscar, setBuscar] = useState('');
  const [origen, setOrigen] = useState<string>(TODOS_ORIGENES);
  const q = useQuery({
    queryKey: ['inventario', 'unidades', inventarioId],
    queryFn: () => getUnidadesArticulo(inventarioId),
    staleTime: 30_000,
  });

  const salidasPorUnidad = useMemo(() => agruparSalidas(q.data?.salidas), [q.data]);

  const unidades = useMemo(() => {
    const todas = q.data?.unidades ?? [];
    const texto = buscar.trim().toLowerCase();
    const filtradas = todas.filter(
      (u) =>
        FILTROS[filtro].pasa(u) &&
        (origen === TODOS_ORIGENES || claveOrigen(u) === origen) &&
        (!texto || (u.nroSerie ?? '').toLowerCase().includes(texto)),
    );
    // En stock: la más vieja primero (el orden en que salen al despachar). El resto: lo último primero.
    return filtro === 'stock' || filtro === 'prestadas' ? filtradas : filtradas.slice().reverse();
  }, [q.data, filtro, buscar, origen]);

  // Orígenes con algo en el filtro de estado actual (importaciones primero, la más nueva arriba).
  const origenes = useMemo(() => {
    const m = new Map<string, { etiqueta: string; cantidad: number; orden: number }>();
    for (const u of q.data?.unidades ?? []) {
      if (!FILTROS[filtro].pasa(u)) continue;
      const k = claveOrigen(u);
      const o = m.get(k) ?? { etiqueta: etiquetaGrupoOrigen(u), cantidad: 0, orden: u.importacion ? -u.importacion.numero : 0 };
      o.cantidad += 1;
      m.set(k, o);
    }
    return [...m].sort((x, y) => x[1].orden - y[1].orden || x[1].etiqueta.localeCompare(y[1].etiqueta, 'es'));
  }, [q.data, filtro]);

  if (q.isPending) return <Skeleton className="h-32 w-full" />;
  if (q.isError) return <div className="text-sm text-destructive">No se pudo cargar el stock por origen.</div>;
  const data = q.data;
  if (!data.habilitado) return <EmptyRow>{NO_VISIBLE}</EmptyRow>;
  if (!data.articulo?.impactaStock) {
    return <EmptyRow>Este artículo no mueve stock por unidades.</EmptyRow>;
  }
  const verCostos = data.unidades.some((u) => u.costoUnitario != null);
  const serializado = data.articulo.serializado;
  const conteo = (f: FiltroUnidades) => data.unidades.filter(FILTROS[f].pasa).length;
  // Sin préstamos no hace falta el filtro.
  const filtros = (Object.keys(FILTROS) as FiltroUnidades[]).filter((f) => f !== 'prestadas' || conteo(f) > 0);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div role="tablist" aria-label={serializado ? 'Filtrar unidades' : 'Filtrar ingresos'} className="flex flex-wrap gap-1.5">
          {filtros.map((f) => (
            <button
              key={f}
              type="button"
              role="tab"
              aria-selected={filtro === f}
              onClick={() => setFiltro(f)}
              className={cn(
                'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                filtro === f
                  ? 'border-foreground bg-foreground text-background'
                  : 'bg-card text-muted-foreground hover:text-foreground',
              )}
            >
              {FILTROS[f].label}
              <span className="ml-1.5 tabular-nums opacity-70">{conteo(f)}</span>
            </button>
          ))}
        </div>
        <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
        {(origenes.length > 1 || origen !== TODOS_ORIGENES) && (
          <Select value={origen} onValueChange={setOrigen}>
            <SelectTrigger className="h-8 w-full text-sm sm:w-56" aria-label="Filtrar por origen">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={TODOS_ORIGENES}>Todos los orígenes</SelectItem>
              {origenes.map(([k, o]) => (
                <SelectItem key={k} value={k}>
                  {o.etiqueta} ({o.cantidad})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {serializado && (
          <div className="relative w-full sm:w-52">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" aria-hidden />
            <Input
              value={buscar}
              onChange={(e) => setBuscar(e.target.value)}
              placeholder="Buscar Nº de serie"
              aria-label="Buscar Nº de serie"
              className="h-8 pl-8 text-sm"
            />
          </div>
        )}
        </div>
      </div>
      {unidades.length === 0 ? (
        <EmptyRow>
          {buscar.trim()
            ? 'Ninguna unidad tiene ese Nº de serie con este filtro.'
            : origen !== TODOS_ORIGENES
              ? 'No hay unidades de ese origen con este filtro.'
            : filtro === 'stock'
              ? 'Sin unidades en stock.'
              : filtro === 'despachadas'
                ? 'Todavía no se despachó ninguna.'
                : 'No hay unidades con este filtro.'}
        </EmptyRow>
      ) : (
        <div className="max-h-[min(20rem,35vh)] overflow-auto rounded-lg border bg-card">
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10 bg-muted text-left text-[11px] uppercase tracking-wide text-muted-foreground shadow-[0_1px_0_0_hsl(var(--border))]">
              <tr>
                <Th>Origen</Th>
                <Th>Llegó</Th>
                <Th>Serie</Th>
                {verCostos && <Th className="text-right">Costo unit.</Th>}
                <Th className="text-right">Recibido</Th>
                <Th className="text-right">Queda</Th>
                <Th>Estado</Th>
                {filtro !== 'stock' && filtro !== 'prestadas' && <Th>A dónde fue</Th>}
              </tr>
            </thead>
            <tbody className="divide-y">
              {unidades.map((u) => (
                <tr key={u.id} className={cn(u.estado === 'agotada' && filtro === 'todas' && 'opacity-60')}>
                  <td className="px-2 py-1.5 align-top">
                    <OrigenCell u={u} links={links} />
                  </td>
                  <td className="whitespace-nowrap px-2 py-1.5 align-top">{fechaUnidad(u.fechaIngreso)}</td>
                  <td className="px-2 py-1.5 align-top">{u.nroSerie || '—'}</td>
                  {verCostos && (
                    <td className="whitespace-nowrap px-2 py-1.5 text-right align-top tabular-nums">
                      {u.costoUnitario != null ? formatMoney(u.costoUnitario, (u.moneda ?? 'ARS') as CurrencyCode) : '—'}
                    </td>
                  )}
                  <td className="px-2 py-1.5 text-right align-top tabular-nums">{num(u.cantidadIngresada)}</td>
                  <td className="px-2 py-1.5 text-right align-top tabular-nums">
                    {num(u.cantidadDisponible)}
                    {u.cantidadPrestada > 0 && (
                      <span className="text-xs text-muted-foreground"> (+{num(u.cantidadPrestada)} prestadas)</span>
                    )}
                  </td>
                  <td className="px-2 py-1.5 align-top">
                    <EstadoPill estado={u.estado} />
                  </td>
                  {filtro !== 'stock' && filtro !== 'prestadas' && (
                    <td className="px-2 py-1.5 align-top">
                      <SalidasLista
                        salidas={salidasPorUnidad.get(u.id) ?? []}
                        vacio={u.cantidadDisponible > 0 ? 'En depósito' : '—'}
                        linkVentas={adminTier && veVentas}
                      />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const TODOS_ORIGENES = 'todos';

/** Grupo de origen de una unidad: su importación, o el tipo de origen si no tiene. */
function claveOrigen(u: UnidadStock): string {
  return u.importacion ? `i${u.importacion.id}` : `o${u.origen}`;
}

function etiquetaGrupoOrigen(u: UnidadStock): string {
  if (u.importacion) return `Importación #${u.importacion.numero}`;
  if (u.origen === 'compra') return 'Compras sin importación';
  if (u.origen === 'stock_inicial') return 'Stock inicial (sin origen)';
  return 'Ajustes manuales';
}

/** Despachos por unidad (las anulaciones van en la misma lista). */
function agruparSalidas(salidas: readonly SalidaUnidad[] | undefined): Map<number, SalidaUnidad[]> {
  const m = new Map<number, SalidaUnidad[]>();
  for (const s of salidas ?? []) m.set(s.unidadId, [...(m.get(s.unidadId) ?? []), s]);
  return m;
}

/** "Despacho 1 un. · 04/11/2026 · Venta 10000800 · Cliente · serie X". */
function SalidasLista({
  salidas,
  vacio,
  linkVentas,
}: {
  salidas: SalidaUnidad[];
  vacio: string;
  linkVentas: boolean;
}) {
  if (salidas.length === 0) return <span className="text-muted-foreground">{vacio}</span>;
  return (
    <ul className="space-y-0.5">
      {salidas.map((s) => (
        <li key={s.movimientoId} className="text-xs">
          <span className="font-medium">{etiquetaSalida(s.tipo)}</span> {num(s.cantidad)} un. · {fechaUnidad(s.fecha)}
          {s.ventaId && (
            <>
              {' · '}
              {linkVentas ? (
                <Link to={`/ventas/${s.ventaId}`} className="text-primary hover:underline">
                  Venta {s.ventaId}
                </Link>
              ) : (
                <>Venta {s.ventaId}</>
              )}
              {s.cliente ? ` · ${s.cliente}` : ''}
            </>
          )}
          {s.nroSerie ? ` · serie ${s.nroSerie}` : ''}
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// Compra / Importación → a dónde fue
// ---------------------------------------------------------------------------

export function OrigenUnidadesSection({
  kind,
  id,
}: {
  kind: 'compra' | 'importacion';
  id: string;
}) {
  const adminTier = useIsAdminTier();
  const veVentas = useCanSeeMenu('ventas');
  const q = useQuery({
    queryKey: ['unidades-origen', kind, id],
    queryFn: () => (kind === 'compra' ? getUnidadesCompra(id) : getUnidadesImportacion(id)),
    staleTime: 30_000,
  });

  // Despachos por unidad (las anulaciones restan).
  const salidasPorUnidad = useMemo(() => agruparSalidas(q.data?.salidas), [q.data]);

  if (q.isPending) return <Skeleton className="h-24 w-full" />;
  if (q.isError) return <div className="text-sm text-destructive">No se pudo cargar el stock recibido.</div>;
  const data = q.data;
  if (!data.habilitado) return null;
  if (data.unidades.length === 0) {
    return (
      <p className="rounded-lg border border-dashed bg-card/40 px-4 py-6 text-center text-sm text-muted-foreground">
        Todavía no ingresó mercadería de {kind === 'compra' ? 'esta compra' : 'esta importación'}. Aparece al
        confirmar un remito en Recepción.
      </p>
    );
  }
  const verCostos = data.unidades.some((u) => u.costoUnitario != null);

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Total titulo="Recibido" valor={num(data.resumen.ingresada)} />
        <Total titulo="Despachado" valor={num(data.resumen.despachada)} />
        <Total titulo="En depósito" valor={num(data.resumen.disponible)} />
        <Total titulo="Prestado / showroom" valor={num(data.resumen.prestada)} />
      </div>
      <div className="overflow-x-auto rounded-lg border bg-card">
        <table className="w-full text-sm">
          <thead className="bg-muted text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <Th>Artículo</Th>
              {kind === 'importacion' && <Th>Compra</Th>}
              <Th>Llegó</Th>
              <Th>Serie</Th>
              {verCostos && <Th className="text-right">Costo unit.</Th>}
              <Th className="text-right">Recibido</Th>
              <Th className="text-right">Queda</Th>
              <Th>A dónde fue</Th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {data.unidades.map((u) => {
              const salidas = salidasPorUnidad.get(u.id) ?? [];
              return (
                <tr key={u.id}>
                  <td className="px-2 py-1.5 align-top font-medium">{u.articulo || '—'}</td>
                  {kind === 'importacion' && (
                    <td className="px-2 py-1.5 align-top text-muted-foreground">
                      {u.compra ? `${u.compra.id}${u.compra.proveedor ? ` · ${u.compra.proveedor}` : ''}` : '—'}
                    </td>
                  )}
                  <td className="whitespace-nowrap px-2 py-1.5 align-top">{fechaUnidad(u.fechaIngreso)}</td>
                  <td className="px-2 py-1.5 align-top">{u.nroSerie || '—'}</td>
                  {verCostos && (
                    <td className="whitespace-nowrap px-2 py-1.5 text-right align-top tabular-nums">
                      {u.costoUnitario != null ? formatMoney(u.costoUnitario, (u.moneda ?? 'ARS') as CurrencyCode) : '—'}
                    </td>
                  )}
                  <td className="px-2 py-1.5 text-right align-top tabular-nums">{num(u.cantidadIngresada)}</td>
                  <td className="px-2 py-1.5 text-right align-top tabular-nums">
                    {num(u.cantidadDisponible)}
                    {u.cantidadPrestada > 0 && (
                      <span className="text-xs text-muted-foreground"> (+{num(u.cantidadPrestada)} prestadas)</span>
                    )}
                  </td>
                  <td className="px-2 py-1.5 align-top">
                    <SalidasLista
                      salidas={salidas}
                      vacio={u.cantidadDisponible > 0 ? 'En depósito' : '—'}
                      linkVentas={adminTier && veVentas}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EmptyRow({ children }: { children: React.ReactNode }) {
  return (
    <p className="rounded-lg border border-dashed px-4 py-6 text-center text-sm text-muted-foreground">{children}</p>
  );
}
